/**
 * Shared primitives, constants & helpers used across Admin Dashboard panels.
 * Behaviour preserved verbatim from the original monolithic Admin.jsx.
 */
import { CircleDot } from "lucide-react";

export const Detail = ({ label, value }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-2 border-b border-[var(--ld-border)]/60 last:border-b-0 text-sm">
    <dt className="text-[var(--ld-ink-muted)] text-[11px] tracking-[0.15em] uppercase pt-1">
      {label}
    </dt>
    <dd className="text-[var(--ld-ink)] whitespace-pre-wrap break-words">
      {Array.isArray(value) ? value.join(", ") : value || "—"}
    </dd>
  </div>
);

export const StatusPill = ({ status }) => {
  const map = {
    pending: "border-[var(--ld-gold-deep)]/60 text-[var(--ld-gold-deep)]",
    approved: "border-emerald-700/40 text-emerald-700",
    rejected: "border-red-700/40 text-red-700",
    new: "border-sky-700/40 text-sky-700",
    contacted: "border-emerald-700/40 text-emerald-700",
    matched: "border-violet-700/40 text-violet-700",
    closed: "border-[var(--ld-ink)]/40 text-[var(--ld-ink-muted)]",
  };
  return (
    <span
      data-testid={`status-${status}`}
      className={
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border " +
        (map[status] || "border-[var(--ld-ink)]/30 text-[var(--ld-ink)]")
      }
    >
      <CircleDot size={10} strokeWidth={1.5} />
      {status}
    </span>
  );
};

export const RolePill = ({ role }) => {
  const isVendor = role === "vendor";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border ${
        isVendor
          ? "border-sky-700/40 text-sky-700"
          : "border-violet-700/40 text-violet-700"
      }`}
    >
      {role}
    </span>
  );
};

export const formatShortDate = (val) => {
  if (!val) return "—";
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const monthsSince = (val) => {
  if (!val) return null;
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  return (
    (now.getFullYear() - d.getFullYear()) * 12 +
    (now.getMonth() - d.getMonth()) +
    (now.getDate() < d.getDate() ? -1 : 0)
  );
};

// Compact "5m ago" / "3h ago" / "2d ago" / "Apr 3" for the last-signin column.
// Returns "Never" for null/invalid so admins can spot dormant accounts.
export const formatRelativeTime = (val) => {
  if (!val) return "Never";
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return "Never";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return formatShortDate(d);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return formatShortDate(d);
};

/**
 * Review state:
 * - manual override wins ("green" | "red")
 * - else: months_since(last_reviewed_at OR accepted_at) >= 10 -> "red", else "green"
 * - if not yet approved (no accepted_at and no override) -> null (n/a)
 */
export const computeReviewState = (app) => {
  if (app.review_override === "green" || app.review_override === "red") {
    return app.review_override;
  }
  const anchor = app.last_reviewed_at || app.accepted_at;
  if (!anchor) return null;
  const m = monthsSince(anchor);
  if (m === null) return null;
  return m >= 10 ? "red" : "green";
};

export const EVENT_COLOR_CLASSES = {
  orange: {
    card: "bg-orange-500/10 border-orange-500 text-orange-900",
    dot: "bg-orange-500",
    label: "Consultation",
  },
  blue: {
    card: "bg-sky-500/10 border-sky-500 text-sky-900",
    dot: "bg-sky-500",
    label: "Vendor introduction to book",
  },
  "dark-blue": {
    card: "bg-sky-900/15 border-sky-800 text-sky-950",
    dot: "bg-sky-800",
    label: "Vendor intro booked",
  },
  purple: {
    card: "bg-purple-500/10 border-purple-500 text-purple-900",
    dot: "bg-purple-500",
    label: "Couple introduction to book",
  },
  "dark-purple": {
    card: "bg-purple-900/15 border-purple-800 text-purple-950",
    dot: "bg-purple-800",
    label: "couple intro booked",
  },
  red: {
    card: "bg-red-500/10 border-red-500 text-red-900",
    dot: "bg-red-500",
    label: "Vendor Consultation",
  },
  gold: {
    card: "bg-amber-500/10 border-amber-600 text-amber-900",
    dot: "bg-amber-600",
    label: "Custom / held",
  },
};

export const colorFor = (e) =>
  EVENT_COLOR_CLASSES[e.color] || EVENT_COLOR_CLASSES.orange;

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const SLOT_KIND_OPTIONS = [
  { value: "vendor_intro_open", label: "Vendor intro · to book", color: "blue" },
  {
    value: "couple_intro_open",
    label: "Couple intro · to book",
    color: "purple",
  },
  { value: "vendor_intro", label: "Vendor Consultation (red)", color: "red" },
  {
    value: "admin_held",
    label: "Custom / held (gold)",
    color: "gold",
  },
];

export const COMING_SOON_COPY = {
  contracts: {
    title: "Contracts",
    blurb:
      "Vendor agreements, couple service contracts, and signed PDFs in one secure place — coming soon.",
  },
};

export const CONSULTATION_SUBTABS = [
  { id: "pending", label: "Pending" },
  { id: "completed-vendors", label: "Completed · Vendors" },
  { id: "completed-couples", label: "Completed · Couples" },
];

/** Status-driven Welcome button styling.
 *  - opened   → green (recipient opened the email)
 *  - sent/delivered → yellow (email landed, awaiting open)
 *  - default  → gold outline (never sent / bounced) */
export const welcomeButtonClass = (status) => {
  if (status === "opened") {
    return "border-emerald-500/60 text-emerald-700 bg-emerald-50 hover:bg-emerald-500 hover:text-white";
  }
  if (status === "sent" || status === "delivered") {
    return "border-amber-400/60 text-amber-800 bg-amber-50 hover:bg-amber-400 hover:text-[var(--ld-ink)]";
  }
  return "border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)] hover:bg-[var(--ld-gold-deep)] hover:text-white";
};

export const welcomeButtonTitle = (status, sentAt, fallbackEmail) => {
  if (status === "opened") return "Vendor opened the welcome email";
  if (status === "sent" || status === "delivered") {
    return `Welcome email sent${sentAt ? " on " + sentAt.slice(0, 10) : ""}`;
  }
  return `Send welcome email to ${fallbackEmail || "this recipient"}`;
};

/** Renders the right-aligned yellow/green tracking dot used on the Send T&C
 *  + Send pack buttons. Yellow = sent/delivered (awaiting open). Green =
 *  opened. Returns null for any other status (none / bounced / failed). */
export const StatusDot = ({ status, testid }) => {
  if (status === "opened") {
    return (
      <span
        data-testid={testid}
        data-status="opened"
        title="Recipient opened the email"
        className="ml-1 inline-block h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-500/25"
      />
    );
  }
  if (status === "sent" || status === "delivered") {
    return (
      <span
        data-testid={testid}
        data-status="sent"
        title="Email delivered — awaiting open"
        className="ml-1 inline-block h-2 w-2 rounded-full bg-amber-400 ring-2 ring-amber-400/30"
      />
    );
  }
  return null;
};

export const ReviewPill = ({ app, onCycle }) => {
  const state = computeReviewState(app);
  if (!state) {
    return (
      <span
        data-testid={`review-pill-${app.id}`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/15 text-[var(--ld-ink-muted)]"
        title="Available after approval"
      >
        <CircleDot size={10} strokeWidth={1.5} />
        n/a
      </span>
    );
  }
  const isRed = state === "red";
  return (
    <button
      type="button"
      onClick={() => onCycle(app)}
      data-testid={`review-pill-${app.id}`}
      data-review-state={state}
      title={
        app.review_override
          ? `Manual override: ${state.toUpperCase()} — click to cycle (one click = green, two = red)`
          : `Auto: ${state.toUpperCase()} based on accepted date`
      }
      className={
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border transition-colors hover:opacity-90 " +
        (isRed
          ? "border-red-500/50 bg-red-500/10 text-red-700"
          : "border-emerald-600/50 bg-emerald-500/10 text-emerald-800")
      }
    >
      <span
        aria-hidden
        className={
          "h-2 w-2 rounded-full " +
          (isRed ? "bg-red-500" : "bg-emerald-500")
        }
      />
      {state}
    </button>
  );
};
